import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, MapPin, Calendar, Clock, Mail } from "lucide-react";
import { CloudCanvas } from "../components/CloudCanvas";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Commande confirmée — Amyu ♡",
    description: "Ta commande Amyu a bien été enregistrée.",
  });

  useEffect(() => {
    axios
      .get(`${API}/orders/${orderId}`)
      .then((r) => setOrder(r.data.order))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-amyu-bg">
        <Loader2 className="animate-spin text-coral" size={32} />
      </div>
    );
  if (!order)
    return (
      <div className="min-h-screen flex items-center justify-center bg-amyu-bg">
        <p className="text-amyu-muted">Commande introuvable</p>
      </div>
    );

  const dateFmt = new Date(order.pickup_date).toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="min-h-screen bg-amyu-bg relative">
      <CloudCanvas />
      <Header />
      <main className="relative z-10 max-w-2xl mx-auto px-5 py-20 md:py-28">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-amyu-pistachio/40 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="text-amyu-pistachio-dark" size={40} strokeWidth={2} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl">
            C&apos;est commandé <span className="italic text-coral">♡</span>
          </h1>
          <p className="mt-3 text-amyu-muted">
            Ta commande <span className="font-medium text-amyu-ink">#{order.ref}</span> a bien été enregistrée.
          </p>
          <p className="mt-2 text-sm text-amyu-muted">
            Un email de confirmation vient de partir vers <span className="font-medium">{order.email}</span>.
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-md rounded-[28px] p-6 md:p-8">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <Calendar size={18} className="text-coral mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">Retrait</div>
                <div className="font-medium capitalize mt-0.5" data-testid="confirmation-date">{dateFmt}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-coral mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">Créneau</div>
                <div className="font-medium mt-0.5" data-testid="confirmation-slot">{order.pickup_slot}</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin size={18} className="text-coral mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted">Lieu</div>
                <div className="font-medium mt-0.5 text-sm">{order.pickup_location}</div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-dashed border-amyu/40">
            <div className="text-[10px] uppercase tracking-[0.16em] text-amyu-muted mb-2">Produits</div>
            {order.items.map((it) => (
              <div key={it.product_id} className="flex justify-between py-1 text-sm">
                <span>{it.quantity} × {it.name}</span>
                <span className="font-medium">{it.line_total.toFixed(2).replace(".", ",")} €</span>
              </div>
            ))}
            <div className="mt-3 pt-3 border-t border-amyu/30 flex justify-between font-display text-xl">
              <span>Total</span>
              <span className="text-coral">{order.subtotal.toFixed(2).replace(".", ",")} €</span>
            </div>
          </div>

          <div className="mt-6 bg-amyu-cream rounded-2xl p-4 text-sm flex gap-3">
            <Mail size={16} className="text-coral mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-amyu-ink">Paiement sur place</div>
              <div className="text-xs text-amyu-muted mt-1">
                Le règlement (espèces ou virement) se fait le jour du retrait. On te confirme le lieu exact par email.
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/"
            data-testid="confirmation-home-link"
            className="inline-flex items-center gap-2 text-coral hover:underline"
          >
            Retour à l&apos;accueil
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
